import React, { useState, useEffect, useMemo } from 'react';
import { Container, Heading, Box, Text, SimpleGrid, Image, Link as ChakraLink, Button, Tooltip, Spinner, Center } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import teamsConfig from '../config/teams.json';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Flex,
  VStack,
  HStack,
  Tag,
  Avatar,
  Divider,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  Wrap,
  WrapItem
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { supabase } from '../supabaseClient';

interface TeamSummary {
  team: string;
  memberCount: number;
  positionCounts: Record<string, number>;
  members: string[];
  totalRaces: number;
}

// Add interface for team config
interface TeamConfig {
  logo?: string;
  website?: string;
}

// Calculate team win rate based on skiing-style points per unique team member.
// Points for positions 1–6: 100, 80, 60, 50, 45, 40.
const calculateTeamWinRate = (
  positionCounts: Record<string, number>,
  memberCount: number
): string => {
  const POINTS_PER_POSITION: Record<string, number> = {
    '1': 100,
    '2': 80,
    '3': 60,
    '4': 50,
    '5': 45,
    '6': 40,
  };

  if (!memberCount || memberCount <= 0) {
    return '0.0';
  }

  const totalPoints =
    (positionCounts['1'] || 0) * POINTS_PER_POSITION['1'] +
    (positionCounts['2'] || 0) * POINTS_PER_POSITION['2'] +
    (positionCounts['3'] || 0) * POINTS_PER_POSITION['3'] +
    (positionCounts['4'] || 0) * POINTS_PER_POSITION['4'] +
    (positionCounts['5'] || 0) * POINTS_PER_POSITION['5'] +
    (positionCounts['6'] || 0) * POINTS_PER_POSITION['6'];

  const winRate = totalPoints / memberCount;

  return winRate.toFixed(1);
};

// Calculate cumulative points earned by the team from positions 1–6.
const calculateTeamPoints = (positionCounts: Record<string, number>): number => {
  const POINTS_PER_POSITION: Record<string, number> = {
    '1': 100,
    '2': 80,
    '3': 60,
    '4': 50,
    '5': 45,
    '6': 40,
  };

  return (
    (positionCounts['1'] || 0) * POINTS_PER_POSITION['1'] +
    (positionCounts['2'] || 0) * POINTS_PER_POSITION['2'] +
    (positionCounts['3'] || 0) * POINTS_PER_POSITION['3'] +
    (positionCounts['4'] || 0) * POINTS_PER_POSITION['4'] +
    (positionCounts['5'] || 0) * POINTS_PER_POSITION['5'] +
    (positionCounts['6'] || 0) * POINTS_PER_POSITION['6']
  );
};

const getTeamConfig = (teamName: string): TeamConfig => {
  const configEntry = Object.entries(teamsConfig).find(
    ([key]) => key.toLowerCase() === teamName?.toLowerCase()
  );
  return configEntry ? configEntry[1] as TeamConfig : {};
};

// Add TeamCard component
const TeamCard = ({ team }: { team: TeamSummary }) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  const teamConfig = getTeamConfig(team.team);
  
  // Calculate statistics consistently with TeamDetail
  const totalRaces = team.totalRaces || Object.values(team.positionCounts).reduce((a, b) => a + b, 0);
  const top3Finishes = (team.positionCounts['1'] || 0) + 
                       (team.positionCounts['2'] || 0) + 
                       (team.positionCounts['3'] || 0);
  const pointsEarned = calculateTeamPoints(team.positionCounts);
  // Win rate is computed from total points (1–6 places) divided by unique team members.
  const winRate = calculateTeamWinRate(team.positionCounts, team.memberCount);
  
  return (
    <Box 
      p={4} 
      borderWidth="1px" 
      borderRadius="lg" 
      overflow="hidden"
      bg={bgColor}
      borderColor={borderColor}
      boxShadow="sm"
      transition="all 0.3s"
      _hover={{ 
        transform: 'translateY(-4px)', 
        boxShadow: 'md',
        borderColor: 'blue.300'
      }}
      onClick={() => navigate(`/teamDetail/${encodeURIComponent(team.team)}`)}
      cursor="pointer"
      height="100%"
    >
      <Flex direction="column" height="100%">
        <Flex align="center" mb={3}>
          <Box 
            width="50px" 
            height="50px" 
            mr={3} 
            borderRadius="md" 
            overflow="hidden"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {teamConfig.logo ? (
              <Image 
                src={teamConfig.logo} 
                alt={`${team.team} logo`} 
                objectFit="contain"
                maxH="45px"
                maxW="45px"
                fallback={
                  <Text fontSize="xl" fontWeight="bold" color="gray.400">
                    {team.team.substring(0, 2)}
                  </Text>
                }
              />
            ) : (
              <Text fontSize="xl" fontWeight="bold" color="gray.400">
                {team.team.substring(0, 2)}
              </Text>
            )}
          </Box>
          <Heading size="md" isTruncated>{team.team}</Heading>
        </Flex>
        
        <Divider mb={3} />
        
        <VStack align="start" spacing={1} mb={3} flex="1">
          <HStack>
            <Text fontWeight="medium" minWidth="120px">Members:</Text>
            <Text>{team.memberCount}</Text>
          </HStack>
          <HStack>
            <Text fontWeight="medium" minWidth="120px">Total Races:</Text>
            <Text>{totalRaces}</Text>
          </HStack>
          <HStack>
            <Text fontWeight="medium" minWidth="120px">Top 3 Finishes:</Text>
            <Text>{top3Finishes}</Text>
          </HStack>
          <HStack>
            <Text fontWeight="medium" minWidth="120px">Points earned:</Text>
            <Text>{pointsEarned}</Text>
          </HStack>
          <Tooltip
            label={(
              <Box fontSize="xs" fontWeight="normal">
                Rate = Members / body za výhry.
                <br />
                1. miesto – 100 bodov, 2. miesto – 80 bodov, 3. miesto – 60 bodov, 4. miesto – 50 bodov, 5. miesto – 45 bodov, 6. miesto – 40 bodov.
              </Box>
            )}
            hasArrow
            placement="top"
          >
            <HStack>
              <Text fontWeight="medium" minWidth="120px">Win Rate:</Text>
              <Text>{winRate}%</Text>
            </HStack>
          </Tooltip>
        </VStack>
        
        {/* Add small position distribution chart */}
        <Box height="100px" mb={3}>
          <Bar 
            data={{
              labels: ['1st', '2nd', '3rd', '4th', '5th', '6th'],
              datasets: [{
                label: 'Positions',
                data: [1,2,3,4,5,6].map(pos => team.positionCounts[pos] || 0),
                backgroundColor: [
                  '#FFD700', // Gold
                  '#C0C0C0', // Silver
                  '#CD7F32', // Bronze
                  '#A0A0A0',
                  '#808080',
                  '#606060',
                ],
              }],
            }} 
            options={{
              responsive: true,
              maintainAspectRatio: false,
              // Disable Chart.js internal pointer events for this tiny inline chart.
              // On mobile, rapid taps could fire Chart.js handlers after navigation,
              // causing null chart metadata errors. We only need the visual here.
              events: [],
              plugins: {
                legend: {
                  display: false
                },
                title: {
                  display: true,
                  text: 'Position Distribution',
                  font: {
                    size: 10
                  }
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    stepSize: 1,
                    font: {
                      size: 8
                    }
                  }
                },
                x: {
                  ticks: {
                    font: {
                      size: 8
                    }
                  }
                }
              }
            }} 
          />
        </Box>
        
        <Button 
          size="sm" 
          colorScheme="blue" 
          width="full"
          mt="auto"
        >
          View Team Details
        </Button>
      </Flex>
    </Box>
  );
};

// Add TeamsOverview component
const TeamsOverview = ({ teamStats }: { teamStats: TeamSummary[] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Derive filtered list during render so it's in sync with teamStats immediately
  // (avoids flash of "No teams found" when transitioning from loading to loaded)
  const filteredTeams = useMemo(() => {
    return teamStats
      .filter(team =>
        team.team.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const totalRacesA = a.totalRaces || Object.values(a.positionCounts).reduce((acc, val) => acc + val, 0);
        const totalRacesB = b.totalRaces || Object.values(b.positionCounts).reduce((acc, val) => acc + val, 0);
        return totalRacesB - totalRacesA;
      });
  }, [teamStats, searchTerm]);

  return (
    <Box mb={8}>
      <InputGroup mb={6}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input 
          placeholder="Search teams..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          borderRadius="full"
        />
      </InputGroup>
      
      {filteredTeams.length === 0 ? (
        <Text textAlign="center" py={4}>No teams found</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {filteredTeams.map(team => (
            <TeamCard key={team.team} team={team} />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default function Teams({ teamStats, isLoading = false }: { teamStats: TeamSummary[]; isLoading?: boolean }) {
  const navigate = useNavigate();

  // Show loading spinner while team stats are being fetched (same UX as Races/Racers)
  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="50vh">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Hero heading – same design as Races and Racers */}
      <Box mb={8} position="relative">
        <Image
          src="/img/teams.jpg"
          alt="Teams"
          width="100%"
          height="300px"
          objectFit="cover"
          objectPosition="center"
          borderRadius="lg"
          fallback={<Box bg="gray.200" h="300px" borderRadius="lg" />}
        />
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          p={6}
          background="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))"
          borderBottomRadius="lg"
        >
          <Heading color="white">Teams</Heading>
        </Box>
      </Box>

      {/* Search component at the top for both versions */}
      <TeamsOverview teamStats={teamStats} />

    </Container>
  );
} 